import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes, useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { MessageCircle, X, Paperclip, Send } from 'react-feather'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import {
  ACCEPTED_MIME,
  MAX_ATTACHMENTS,
  OutgoingAttachment,
  SupportMessage,
  fetchSupportThread,
  readAttachment,
  readStoredToken,
  sendSupportMessage
} from 'utils/support/supportApi'

/* ── motion ─────────────────────────────────────────────────────────────── */

/** A slow breath, so the button reads as available rather than urgent. */
const breathe = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50%      { transform: translateY(-3px) scale(1.03); }
`

/** One ring leaving the button, on a long cycle — a hint, not a nag. */
const halo = keyframes`
  0%   { opacity: 0.45; transform: scale(1); }
  70%  { opacity: 0;    transform: scale(1.6); }
  100% { opacity: 0;    transform: scale(1.6); }
`

/** The panel grows out of the button it came from, not out of nowhere. */
const openPanel = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.94); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`

const fadeInMessage = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ── chrome ─────────────────────────────────────────────────────────────── */

const Anchor = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.mobile} {
    right: 16px;
    bottom: 16px;
  }
`

const Launcher = styled.button<{ $open: boolean }>`
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.purple};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  animation: ${({ $open }) => ($open ? 'none' : breathe)} 3.6s ease-in-out infinite;
  transition: transform 160ms ease, background 160ms ease;

  &:hover {
    transform: scale(1.06);
  }
  &:active {
    transform: scale(0.96);
  }

  /* The halo only exists while the panel is closed — once somebody is
     reading, asking for attention again is just noise. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.colors.purple};
    animation: ${({ $open }) => ($open ? 'none' : halo)} 3.6s ease-out infinite;
    pointer-events: none;
  }

  /* Respect people who asked their system for less movement. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &::after {
      animation: none;
      opacity: 0;
    }
  }
`

const Unread = styled(Flex)`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.cyan};
  color: ${({ theme }) => theme.colors.black ?? '#000'};
  font-size: 11px;
  font-weight: 700;
`

const Panel = styled(Flex)`
  width: 420px;
  /* A chat needs room to be a chat: short panels turn a conversation into a
     form with history stapled underneath. */
  height: min(78vh, 680px);
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55);
  transform-origin: bottom right;
  animation: ${openPanel} 220ms cubic-bezier(0.16, 1, 0.3, 1);

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: calc(100vw - 32px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Header = styled(Flex)`
  padding: 16px 18px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
`

const Body = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  font-size: 15px;
`

const Bubble = styled(Flex)<{ $mine: boolean }>`
  max-width: 88%;
  flex-direction: column;
  gap: 5px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 15px;
  line-height: 1.45;
  align-self: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  background: ${({ theme, $mine }) =>
    $mine ? theme.colors.purple : theme.colors.background};
  border: 1px solid
    ${({ theme, $mine }) => ($mine ? 'transparent' : theme.colors.gray8)};
  animation: ${fadeInMessage} 200ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Composer = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray8};
  flex: 0 0 auto;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  resize: vertical;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 15px;
  line-height: 1.45;
  font-family: inherit;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const AttachRow = styled(Flex)`
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`

const Chip = styled(Flex)`
  gap: 6px;
  align-items: center;
  padding: 5px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  font-size: 13px;
  /* Without this the filename inherits the page's default ink and vanishes
     against the chip: an attachment that shows as an empty box gives the
     person no way to tell what they are about to send. */
  color: ${({ theme }) => theme.colors.white};
  max-width: 220px;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

/** The house rules, once, in the flow of the conversation. */
const StandingNote = styled(Flex)`
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px dashed ${({ theme }) => theme.colors.gray8};
  flex: 0 0 auto;
`

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  color: ${({ theme }) => theme.colors.gray6};
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

/* ── component ──────────────────────────────────────────────────────────── */

/** While the panel is open, watch for a reply without hammering the server. */
const POLL_MS = 20000

/**
 * The community help channel.
 *
 * Only rendered for a connected wallet: every conversation is keyed by one,
 * and a report we cannot tie to an account is a report we cannot act on. It is
 * a thread rather than a form because that is the promise made when somebody
 * writes — the answer comes back to the same place they asked.
 */
const SupportWidget: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account } = useWeb3React()

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState<OutgoingAttachment[]>([])
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [unread, setUnread] = useState(0)
  const fileInput = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  /**
   * How many moderator messages this person has already read.
   *
   * Kept in storage, not just in memory: held only in a ref it reset to zero
   * on every page load, so a reply read yesterday raised the badge again on
   * every visit — a notification that never goes away teaches people to
   * ignore notifications.
   */
  const seenKey = account ? `mprolab.support.seen.${account.toLowerCase()}` : null
  const readSeen = useCallback((): number => {
    if (!seenKey) return 0
    try {
      return Number(window.localStorage.getItem(seenKey) ?? 0) || 0
    } catch {
      return 0
    }
  }, [seenKey])
  const writeSeen = useCallback(
    (count: number) => {
      if (!seenKey) return
      try {
        window.localStorage.setItem(seenKey, String(count))
      } catch {
        /* private windows: the badge simply returns next visit */
      }
    },
    [seenKey]
  )

  const loadThread = useCallback(async () => {
    const token = readStoredToken(account)
    if (!token) return
    const thread = await fetchSupportThread(token)
    if (!thread) return
    setMessages(thread.messages)
    const replies = thread.messages.filter((m) => m.author === 'moderator').length
    if (open) {
      writeSeen(replies)
      setUnread(0)
      return
    }
    setUnread(Math.max(0, replies - readSeen()))
  }, [account, open, readSeen, writeSeen])

  // A reply that arrives while somebody is elsewhere in the app should still
  // reach them, so the check runs on mount and not only when the panel opens.
  useEffect(() => {
    if (!account) return undefined
    void loadThread()
    const timer = setInterval(() => void loadThread(), POLL_MS)
    return () => clearInterval(timer)
  }, [account, loadThread])

  useEffect(() => {
    if (!open) return
    setUnread(0)
    // Opening the panel IS reading it, and that has to be remembered across
    // reloads rather than only for this page view.
    writeSeen(messages.filter((m) => m.author === 'moderator').length)
    // Newest message in view when the panel opens.
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
  }, [open, messages, writeSeen])

  const pickFiles = useCallback(
    async (list: FileList | null) => {
      if (!list) return
      const room = MAX_ATTACHMENTS - attachments.length
      if (room <= 0) return
      const picked: OutgoingAttachment[] = []
      let rejected = false
      for (const file of Array.from(list).slice(0, room)) {
        const read = await readAttachment(file)
        if (read) picked.push(read)
        else rejected = true
      }
      if (picked.length) setAttachments((a) => [...a, ...picked])
      // Say why something did not attach rather than dropping it in silence.
      setNotice(rejected ? t('support.attachmentRejected') : null)
    },
    [attachments.length, t]
  )

  const onSend = useCallback(async () => {
    const body = text.trim()
    if (!body || !account || sending) return
    setSending(true)
    setNotice(null)
    // Show it immediately: the message is theirs whether or not our server is
    // having a good day, and a thread that stays empty after pressing send
    // reads as a loss.
    const optimistic: SupportMessage = {
      author: 'user',
      text: body,
      ts: new Date().toISOString(),
      attachments: attachments.map((a) => ({ name: a.name, mime: a.mime, size: 0 }))
    }
    setMessages((m) => [...m, optimistic])
    setText('')
    setAttachments([])

    const result = await sendSupportMessage({
      wallet: account,
      message: body,
      attachments
    })
    setSending(false)
    if (!result.accepted) {
      setNotice(t('support.sendFailed'))
      // Take the optimistic message back out; leaving it would claim we have
      // something we do not.
      setMessages((m) => m.filter((x) => x !== optimistic))
      setText(body)
      return
    }
    setNotice(t('support.thanks'))
    void loadThread()
  }, [text, account, sending, attachments, t, loadThread])

  // No wallet, no widget: a conversation with nobody in particular cannot be
  // answered, and the address is what makes a report actionable.
  if (!account) return null

  return (
    <Anchor>
      {open && (
        <Panel>
          <Header>
            <Flex flexDirection="column">
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
                {t('support.title')}
              </Text>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {`${account.slice(0, 6)}…${account.slice(-4)}`}
              </Text>
            </Flex>
            <IconButton onClick={() => setOpen(false)} aria-label={t('support.close')}>
              <X size={18} />
            </IconButton>
          </Header>

          <Body ref={bodyRef}>
            {/*
              Said once, at the top of the conversation, and scrolling away
              with it. Pinned it was a permanent strip of small print eating
              the room the messages need.
            */}
            <StandingNote>
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('support.moderators')}
              </Text>
            </StandingNote>

            {messages.length === 0 ? (
              <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6}>
                {t('support.greeting')}
              </Text>
            ) : (
              messages.map((m, i) => (
                <Bubble key={`${m.ts}-${i}`} $mine={m.author === 'user'}>
                  <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                    {m.author === 'user'
                      ? t('support.you')
                      : t('support.moderator')}
                  </Text>
                  <Text
                    variant={TextVariant.BODY_DEFAULT}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {m.text}
                  </Text>
                  {m.attachments?.length > 0 && (
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {m.attachments.map((a) => a.name).join(', ')}
                    </Text>
                  )}
                </Bubble>
              ))
            )}

          </Body>

          <Composer>
            {/*
              Anchored to the composer, because it is about what just happened
              when they pressed send. Inside the message flow it drifted below
              later replies and read as though it had arrived after them.
            */}
            {notice && (
              <Text variant={TextVariant.BODY_SMALL} color={colors.cyan}>
                {notice}
              </Text>
            )}
            <TextArea
              value={text}
              placeholder={t('support.placeholder')}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter is a new line — what a chat box does.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void onSend()
                }
              }}
            />
            <AttachRow>
              <IconButton
                onClick={() => fileInput.current?.click()}
                aria-label={t('support.attach')}
              >
                <Paperclip size={18} />
              </IconButton>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ACCEPTED_MIME.join(',')}
                style={{ display: 'none' }}
                onChange={(e) => {
                  void pickFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              {attachments.map((a, i) => (
                <Chip key={`${a.name}-${i}`}>
                  <span>{a.name}</span>
                  <IconButton
                    onClick={() =>
                      setAttachments((list) => list.filter((_, j) => j !== i))
                    }
                    aria-label={t('support.removeAttachment')}
                  >
                    <X size={12} />
                  </IconButton>
                </Chip>
              ))}
              <Flex flex="1" />
              <Button
                size="sm"
                onClick={() => void onSend()}
                disabled={sending || !text.trim()}
                endIcon={<Send size={14} />}
              >
                {sending ? t('support.sending') : t('support.send')}
              </Button>
            </AttachRow>
          </Composer>
        </Panel>
      )}

      <Launcher
        $open={open}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t('support.close') : t('support.title')}
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
        {!open && unread > 0 && <Unread>{unread}</Unread>}
      </Launcher>
    </Anchor>
  )
}

export default SupportWidget
